/**
 * @author: leroy
 * @date: 2021/11/18 14:32
 * @description：common
 */
import { getServerTime, getSTSToken } from '@/store/services/common';
import { makeAutoObservable, runInAction } from 'mobx';
import { enableStaticRendering } from 'mobx-react';
import type { Moment } from 'moment';
import moment from 'moment';
import { timerWorker } from '@/utils/worker';

enableStaticRendering(typeof window === 'undefined');

export class CommonStore {
  difference: number = 0;
  serverTime: Moment | null = null;
  isGetServerTime = false;
  sts: API.Common.STS = {
    SecurityToken: '',
    AccessKeyId: '',
    AccessKeySecret: '',
    Expiration: '',
  };
  worker: Worker | null = null;

  setSTS = (data: API.Common.STS | undefined) => {
    if (data)
      Object.entries(data).forEach(([key, value]) => {
        this.sts[key] = value;
      });
  };

  // 获取STS参数
  fetchSTS = async () => {
    try {
      const { data } = await getSTSToken();
      runInAction(() => {
        this.setSTS(data);
      });
      return data;
    } catch (error) {
      console.log('fetchSTS', error);
    }
  };

  // 本地跑时间
  runServerTime = () => {
    const workerBlob = new Blob([`(${timerWorker.toString()})()`], {
      type: 'text/javascript',
    });
    if (!this.worker) this.worker = new Worker(URL.createObjectURL(workerBlob));
    this.worker.postMessage({ cmd: 'stop', interval: 1000 });
    this.worker.postMessage({ cmd: 'start', interval: 1000 });
    this.worker.onmessage = () => {
      this.serverTime = this.serverTime?.add(1, 'second') || null;
    };
  };

  // 获取服务端时间
  fetchServerTime = async () => {
    try {
      const { data } = await getServerTime();
      runInAction(() => {
        this.serverTime = moment(data?.date);
        this.difference = moment().diff(data?.date.concat('+08:00'));
        console.log('服务器时间', { severTime: data?.date, difference: this.difference });
        this.isGetServerTime = true;
        this.runServerTime();
      });
      return data;
    } catch (error) {
      console.log('fetchServerTime', error);
    }
  };

  // 更新时间
  updateTime = (string) => {
    this.serverTime = moment(string);
    this.difference = moment().diff(this.serverTime);
    this.runServerTime();
  };

  // 获取当前时间(东8区)
  get nowTime() {
    return this.serverTime || moment().subtract(this.difference, 'milliseconds');
  }

  // oss
  get oss() {
    if (typeof window === 'undefined') return {};
    return window.OSS({
      // yourRegion填写Bucket所在地域。以华东1（杭州）为例，Region填写为oss-cn-hangzhou。
      region: 'oss-cn-shanghai',
      // 从STS服务获取的临时访问密钥（AccessKey ID和AccessKey Secret）。
      accessKeyId: this.sts.AccessKeyId,
      accessKeySecret: this.sts.AccessKeySecret,
      // 从STS服务获取的安全令牌（SecurityToken）。
      stsToken: this.sts.SecurityToken,
      // 填写Bucket名称。
      bucket: 'ht-exam',
      refreshSTSToken: async () => {
        const res = await this.fetchSTS();
        return {
          stsToken: res?.SecurityToken || '',
          accessKeyId: res?.AccessKeyId || '',
          accessKeySecret: res?.AccessKeySecret || '',
        };
      },
    });
  }

  // 检查 sts 是否过期
  checkExpired = async () => {
    const isExpired = this.nowTime.isAfter(this.sts.Expiration);
    if (this.sts.Expiration && !isExpired) {
      return true;
    }
    await this.fetchSTS();
    return true;
  };

  // 上传
  uploadFile = async (file: Blob, url: string) => {
    await this.checkExpired();
    try {
      const result = await this.oss.put(url, file);
      return result;
    } catch (error) {
      console.log('uploadFile', error);
      return undefined;
    }
  };

  // 服务端数据
  hydrate = (data) => {
    if (!data) return;
    Object.entries(data).forEach(([key, value]) => {
      this[key] = value;
    });
  };

  constructor() {
    makeAutoObservable(this);
  }
}

export type CommonState = Partial<InstanceType<typeof CommonStore>>;
